import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { injectIntl, intlShape } from 'react-intl';
import { theme } from '@hey-car/ui';
/**
 * FYI: This dependency was added on behalf of: https://heycar.atlassian.net/browse/PROMO-1614
 *
 * Since it's quite tiny (773B MINIFIED + GZIPPED), we can also hard code the related
 * styles in App.css.js file in the future (maybe)?
 *
 * See: https://github.com/necolas/normalize.css/blob/master/normalize.css
 */
import 'normalize.css';

import {
  CookieConsentShape,
  handleCookiesForCookieState,
} from './dealer/cookieDashboard/utils';
import { setGUID } from './shared/modules/tracking/actions';
import {
  setTrackingVersion,
  trackConnectionQuality,
  trackIdentify,
  trackTimeToInteraction,
} from './shared/utils/tracking';
import { applyErrorBoundary } from './shared/components/ErrorBoundary/ErrorBoundary';
import { ConnectedGlobalEventHandlers } from './shared/modules/globalEvents/GlobalEventHandlers/ConnectedGlobalEventHandlers';
import { updatePreviousLocation } from './actions';
import { AppSt, GlobalStyle } from './App.css';
import { setUserCreated, setUserState } from './shared/modules/user/actions';
import { ConnectedOverlayRoot } from './shared/modules/overlay/OverlayRoot/ConnectedOverlayRoot';
import GridDebugger from './dealer/shared/components/Grid/dev/GridDebugger';

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      setUserCreated: setUserCreatedAction,
      setUserState: setUserStateAction,
      setGUID: setGUIDAction,
      createdAt,
      guid,
    } = props;

    // use this to update the user state based on current state of the app (time, existing guid, etc)
    setUserCreatedAction();
    setUserStateAction();

    if (guid === null) {
      setGUIDAction();
    } else {
      trackIdentify(guid, createdAt);
    }

    setTrackingVersion();
  }

  componentDidMount() {
    const { cookiePreferences, refreshToken } = this.props;
    trackConnectionQuality();
    trackTimeToInteraction();

    handleCookiesForCookieState(cookiePreferences);

    if (window.localStorage.getItem('showLogin')) {
      refreshToken();
    }
  }
  componentDidUpdate(prevProps) {
    const { cookiePreferences } = this.props;

    if (cookiePreferences !== prevProps.cookiePreferences) {
      let hasSomeCookiesDisabled = false;

      if (
        !cookiePreferences.marketing &&
        cookiePreferences.marketing !== prevProps.cookiePreferences.marketing
      ) {
        hasSomeCookiesDisabled = true;
      }

      handleCookiesForCookieState(cookiePreferences);
      if (hasSomeCookiesDisabled) {
        window.location.reload();
      }
    }
  }

  render() {
    const { children } = this.props;

    return (
      <ThemeProvider theme={theme}>
        <>
          {process.env.NODE_ENV === 'development' && <GridDebugger />}
          <AppSt>
            <ConnectedOverlayRoot />
            <ConnectedGlobalEventHandlers />
            {children}
          </AppSt>
          <GlobalStyle />
        </>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  // functions
  refreshToken: PropTypes.func,
  setUserCreated: PropTypes.func.isRequired,
  setUserState: PropTypes.func.isRequired,
  updatePreviousLocation: PropTypes.func,

  // properties
  children: PropTypes.node,
  cookiePreferences: CookieConsentShape,
  createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dealerLoggedIn: PropTypes.bool.isRequired,
  guid: PropTypes.string,
  intl: intlShape.isRequired,
  location: PropTypes.object,
  route: PropTypes.object,
  setGUID: PropTypes.func,
};

export const ConnectedAppWithErrorBoundary = applyErrorBoundary(
  connect(
    state => ({
      dealerLoggedIn: state.dealerLogin.userLoggedIn,
      guid: state.tracking.guid,
      cookiePreferences: state.cookieDashboard.preferences,
      hasSavedCookieSettings: state.cookieDashboard.hasSavedCookieSettings,
      createdAt: state.user.createdAt,
    }),
    {
      setGUID,
      updatePreviousLocation,
      setUserCreated,
      setUserState,
    },
  )(injectIntl(App)),
);
